import React, { useCallback, useEffect, useState } from "react";
import BreadcrumbItem from "../../shared/components/BreadcrumbItem";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IBeacon } from "../../shared/interfaces/beacon/IBeacon";
import { BeaconService } from "./service";
import { handleError } from "../../shared/functions/handleError";
import { usePagination } from "../../shared/hooks/usePagination";
import BeaconList from "./components/List";
import { Button, Card, CardHeader, Col, Form, Row } from "react-bootstrap";
import { IPagination } from "../../shared/interfaces/shared/IPagination";
import AsyncSelect2 from "../../shared/components/AsyncSelect2";
import { InstitutionService } from "../institutions/service";
import { IInstitution } from "../../shared/interfaces/institution/IInstitution";
import { ISelectOption } from "../../shared/interfaces/shared/ISelectOption";

const Beacons = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [beacons, setBeacons] = React.useState<IBeacon[]>([]);
  const [defaultInstitutionOptions, setDefaultInstitutionOptions] = useState<ISelectOption<number>[]>([]);

  const {
    pagination,
    setPagination,
    changeFilterHandler,
    handleUpdateList,
    totalPages,
    setTotalPages,
  } = usePagination({
    perPage: 10,
  });

  const [localPagination, setLocalPagination] = useState<IPagination | null>(
    null,
  );

  // Load institutions from API with search query
  const loadInstitutions = useCallback(async (inputValue: string): Promise<ISelectOption<number>[]> => {
    try {
      const response = await InstitutionService.getAll({
        page: 0,
        size: 50,
        name: inputValue || undefined,
      });
      return (response?.data || [])
        .filter((institution: IInstitution) => institution.id !== undefined)
        .map((institution: IInstitution) => ({
          value: institution.id as number,
          label: institution.name,
        }));
    } catch (error) {
      handleError(error);
      return [];
    }
  }, []);

  const changeLocalPaginationHandler = (
    name: string,
    value: string | number | boolean | number[] | null,
  ) => {
    setLocalPagination((prev) => ({ ...prev, [name]: value }) as IPagination);
  };

  useEffect(() => {
    BeaconService.getAll(pagination)
      .then((response) => {
        setBeacons(response?.data);
        setTotalPages(response?.totalPages);
      })
      .catch(handleError);
  }, [pagination, setTotalPages]);

  useEffect(() => {
    loadInstitutions("").then(setDefaultInstitutionOptions);
  }, [loadInstitutions]);

  return (
    <React.Fragment>
      <BreadcrumbItem
        mainTitle={t("beacon.beacons")}
        subTitle={t("beacon.beacons")}
        buttonTitle={t("beacon.addBeacon")}
        onCtaClick={() => navigate("/beacons/create")}
      />
      <Card>
        <Card.Header>
          <h5>{t("beacon.filterBeacons")}</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={3}>
              <Form.Control
                placeholder={t("beacon.searchByTitle")}
                name={"title"}
                value={localPagination?.title ?? ""}
                onChange={(e) =>
                  changeLocalPaginationHandler(e.target.name, e.target.value)
                }
                className={"w-100"}
              />
            </Col>
            <Col md={3}>
              <Form.Control
                placeholder={t("beacon.searchByUuid")}
                name={"uuid"}
                value={localPagination?.uuid ?? ""}
                onChange={(e) =>
                  changeLocalPaginationHandler(e.target.name, e.target.value)
                }
              />
            </Col>
            <Col md={3}>
              <AsyncSelect2<number>
                name="institutionId"
                placeholder={t("general.selectInstitution")}
                loadOptions={loadInstitutions}
                defaultOptions={defaultInstitutionOptions}
                onChange={(value) => {
                  setLocalPagination(
                    (prev) =>
                      ({ ...prev, institutionId: value ?? undefined }) as IPagination,
                  );
                }}
                value={localPagination?.institutionId}
                isClearable
              />
            </Col>
          </Row>
        </Card.Body>
        <Card.Footer className={"text-end"}>
          <Button
            variant={"light"}
            onClick={() => {
              setLocalPagination(null);
              setPagination({ page: 0, size: 20 });
            }}
          >
            {t("button.reset")}
          </Button>
          <Button
            variant={"primary"}
            className={"ms-3"}
            onClick={() => {
              setPagination((prev: IPagination) => ({
                ...prev,
                ...localPagination,
              }));
            }}
          >
            {t("button.search")}
          </Button>
        </Card.Footer>
      </Card>
      <Card className="table-card">
        <CardHeader>
          <h5>{t("beacon.beaconList")}</h5>
        </CardHeader>

        <Card.Body className="pt-3">
          <BeaconList
            beacons={beacons}
            pagination={pagination}
            totalPages={totalPages}
            handleUpdateList={handleUpdateList}
            changeFilterHandler={changeFilterHandler}
          />
        </Card.Body>
      </Card>
    </React.Fragment>
  );
};

export default Beacons;
